import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Badge, Tooltip } from "antd";
import { WarningOutlined } from "@ant-design/icons";
import { api, type SystemHealth } from "../api";
import { useRole } from "../contexts/AuthContext";

const POLL_MS = 5 * 60 * 1000;

// Header indicator, shown only while a system health check is failing, so a
// broken mirror or a silent sensor is noticed even when no Telegram channel
// receives system alerts. A failed poll keeps the last known state.
export default function SystemHealthIndicator({ compact }: { compact: boolean }) {
  const nav = useNavigate();
  const { isAdmin } = useRole();
  const [health, setHealth] = useState<SystemHealth | null>(null);

  useEffect(() => {
    let alive = true;
    const load = () => {
      api
        .healthSystem()
        .then((h) => {
          if (alive) setHealth(h);
        })
        .catch(() => undefined);
    };
    load();
    const id = setInterval(load, POLL_MS);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, []);

  if (!health || health.failing === 0) return null;
  const failing = health.checks.filter((c) => c.status === "failing");

  // Settings is admin-only (App.tsx redirects anyone else to "/"), so only
  // an admin's click opens it; for others the indicator is informational —
  // its tooltip lists what is failing.
  return (
    <Tooltip
      title={
        <div>
          {failing.map((c) => (
            <div key={c.key}>
              {c.name}: {c.detail || "failing"}
            </div>
          ))}
        </div>
      }
    >
      <button
        onClick={isAdmin ? () => nav("/settings") : undefined}
        style={{
          background: "transparent",
          border: "none",
          cursor: isAdmin ? "pointer" : "default",
          padding: "4px 8px",
          borderRadius: 6,
          color: "var(--red)",
          display: "flex",
          alignItems: "center",
          gap: 4,
          fontSize: 13,
        }}
      >
        <Badge count={health.failing} size="small">
          <WarningOutlined style={{ fontSize: 16, color: "var(--red)" }} />
        </Badge>
        {!compact && <span style={{ marginLeft: 6 }}>System</span>}
      </button>
    </Tooltip>
  );
}
